import { getSupportedLocale } from './getSupportedLocale';
import { pseudoBlockifyMessages, pseudoTripleBracketMessages } from './pseudoLocales';
import { logError } from './logger';

/**
 * Extract messages from a dynamic import result.
 * Handles both ESM default exports (webpack) and CJS module.exports (Atlaspack).
 * - Webpack wraps JSON in { default: {...} }
 * - Atlaspack transforms JSON to module.exports = {...} which may not have .default
 */
function resolveMessages(mod: any): Record<string, string> {
	return mod?.default ?? mod;
}

/**
 * Returns all i18n import promises for a given locale — extension messages plus the
 * conversation-assistant and townsquare home/status sub-package messages.
 *
 * NOTE: the townsquare packages do not ship `hr`, `sl` or `sr` bundles, so those cases
 * intentionally omit them. A missing import would reject the `Promise.all` in
 * `loadLocaleData` and fall the whole locale back to English; omitting it instead lets
 * react-intl render just those packages' English `defaultMessage`.
 *
 * Import specifiers must stay static string literals so the bundler can code-split them.
 */
function getMessageLoaders(localeToLoad: string): Array<Promise<any>> {
	switch (localeToLoad) {
		case 'cs':
			return [
				import('@atlassian/afm-i18n-rovo-extension/i18n/cs'),
				import('@atlassian/conversation-assistant/i18n/cs'),
				import('@atlassian/conversation-assistant-agent/i18n/cs'),
				import('@atlassian/conversation-assistant-ui-components/i18n/cs'),
				import('@atlassian/conversation-assistant-chat-prompt-input/i18n/cs'),
				import('@atlassian/home-recents/i18n/cs'),
				import('@atlassian/home-assigned/i18n/cs'),
				import('@atlassian/home-list/i18n/cs'),
				import('@atlassian/home-list-ui/i18n/cs'),
				import('@atlassian/goal-status/i18n/cs'),
				import('@atlassian/project-status/i18n/cs'),
			];
		case 'da':
			return [
				import('@atlassian/afm-i18n-rovo-extension/i18n/da'),
				import('@atlassian/conversation-assistant/i18n/da'),
				import('@atlassian/conversation-assistant-agent/i18n/da'),
				import('@atlassian/conversation-assistant-ui-components/i18n/da'),
				import('@atlassian/conversation-assistant-chat-prompt-input/i18n/da'),
				import('@atlassian/home-recents/i18n/da'),
				import('@atlassian/home-assigned/i18n/da'),
				import('@atlassian/home-list/i18n/da'),
				import('@atlassian/home-list-ui/i18n/da'),
				import('@atlassian/goal-status/i18n/da'),
				import('@atlassian/project-status/i18n/da'),
			];
		case 'de':
			return [
				import('@atlassian/afm-i18n-rovo-extension/i18n/de'),
				import('@atlassian/conversation-assistant/i18n/de'),
				import('@atlassian/conversation-assistant-agent/i18n/de'),
				import('@atlassian/conversation-assistant-ui-components/i18n/de'),
				import('@atlassian/conversation-assistant-chat-prompt-input/i18n/de'),
				import('@atlassian/home-recents/i18n/de'),
				import('@atlassian/home-assigned/i18n/de'),
				import('@atlassian/home-list/i18n/de'),
				import('@atlassian/home-list-ui/i18n/de'),
				import('@atlassian/goal-status/i18n/de'),
				import('@atlassian/project-status/i18n/de'),
			];
		case 'en-GB':
		case 'en-AU':
		case 'en-IN':
		case 'en-NZ':
			return [
				import('@atlassian/afm-i18n-rovo-extension/i18n/en_GB'),
				import('@atlassian/conversation-assistant/i18n/en_GB'),
				import('@atlassian/conversation-assistant-agent/i18n/en_GB'),
				import('@atlassian/conversation-assistant-ui-components/i18n/en_GB'),
				import('@atlassian/conversation-assistant-chat-prompt-input/i18n/en_GB'),
				import('@atlassian/home-recents/i18n/en_GB'),
				import('@atlassian/home-assigned/i18n/en_GB'),
				import('@atlassian/home-list/i18n/en_GB'),
				import('@atlassian/home-list-ui/i18n/en_GB'),
				import('@atlassian/goal-status/i18n/en_GB'),
				import('@atlassian/project-status/i18n/en_GB'),
			];
		case 'en-XA':
			return [
				import('@atlassian/afm-i18n-rovo-extension/i18n/en').then((mod) =>
					pseudoBlockifyMessages(resolveMessages(mod)),
				),
				import('@atlassian/conversation-assistant/i18n/en').then((mod) =>
					pseudoBlockifyMessages(resolveMessages(mod)),
				),
				import('@atlassian/conversation-assistant-agent/i18n/en').then((mod) =>
					pseudoBlockifyMessages(resolveMessages(mod)),
				),
				import('@atlassian/conversation-assistant-ui-components/i18n/en').then((mod) =>
					pseudoBlockifyMessages(resolveMessages(mod)),
				),
				import('@atlassian/conversation-assistant-chat-prompt-input/i18n/en').then((mod) =>
					pseudoBlockifyMessages(resolveMessages(mod)),
				),
				import('@atlassian/home-recents/i18n/en').then((mod) =>
					pseudoBlockifyMessages(resolveMessages(mod)),
				),
				import('@atlassian/home-assigned/i18n/en').then((mod) =>
					pseudoBlockifyMessages(resolveMessages(mod)),
				),
				import('@atlassian/home-list/i18n/en').then((mod) =>
					pseudoBlockifyMessages(resolveMessages(mod)),
				),
				import('@atlassian/home-list-ui/i18n/en').then((mod) =>
					pseudoBlockifyMessages(resolveMessages(mod)),
				),
				import('@atlassian/goal-status/i18n/en').then((mod) =>
					pseudoBlockifyMessages(resolveMessages(mod)),
				),
				import('@atlassian/project-status/i18n/en').then((mod) =>
					pseudoBlockifyMessages(resolveMessages(mod)),
				),
			];
		case 'en-XB':
			return [
				import('@atlassian/afm-i18n-rovo-extension/i18n/en').then((mod) =>
					pseudoTripleBracketMessages(resolveMessages(mod)),
				),
				import('@atlassian/conversation-assistant/i18n/en').then((mod) =>
					pseudoTripleBracketMessages(resolveMessages(mod)),
				),
				import('@atlassian/conversation-assistant-agent/i18n/en').then((mod) =>
					pseudoTripleBracketMessages(resolveMessages(mod)),
				),
				import('@atlassian/conversation-assistant-ui-components/i18n/en').then((mod) =>
					pseudoTripleBracketMessages(resolveMessages(mod)),
				),
				import('@atlassian/conversation-assistant-chat-prompt-input/i18n/en').then((mod) =>
					pseudoTripleBracketMessages(resolveMessages(mod)),
				),
				import('@atlassian/home-recents/i18n/en').then((mod) =>
					pseudoTripleBracketMessages(resolveMessages(mod)),
				),
				import('@atlassian/home-assigned/i18n/en').then((mod) =>
					pseudoTripleBracketMessages(resolveMessages(mod)),
				),
				import('@atlassian/home-list/i18n/en').then((mod) =>
					pseudoTripleBracketMessages(resolveMessages(mod)),
				),
				import('@atlassian/home-list-ui/i18n/en').then((mod) =>
					pseudoTripleBracketMessages(resolveMessages(mod)),
				),
				import('@atlassian/goal-status/i18n/en').then((mod) =>
					pseudoTripleBracketMessages(resolveMessages(mod)),
				),
				import('@atlassian/project-status/i18n/en').then((mod) =>
					pseudoTripleBracketMessages(resolveMessages(mod)),
				),
			];
		case 'en-ZZ':
			return [
				import('@atlassian/afm-i18n-rovo-extension/i18n/en_ZZ'),
				import('@atlassian/conversation-assistant/i18n/en_ZZ'),
				import('@atlassian/conversation-assistant-agent/i18n/en_ZZ'),
				import('@atlassian/conversation-assistant-ui-components/i18n/en_ZZ'),
				import('@atlassian/conversation-assistant-chat-prompt-input/i18n/en_ZZ'),
				import('@atlassian/home-recents/i18n/en_ZZ'),
				import('@atlassian/home-assigned/i18n/en_ZZ'),
				import('@atlassian/home-list/i18n/en_ZZ'),
				import('@atlassian/home-list-ui/i18n/en_ZZ'),
				import('@atlassian/goal-status/i18n/en_ZZ'),
				import('@atlassian/project-status/i18n/en_ZZ'),
			];
		case 'es':
			return [
				import('@atlassian/afm-i18n-rovo-extension/i18n/es'),
				import('@atlassian/conversation-assistant/i18n/es'),
				import('@atlassian/conversation-assistant-agent/i18n/es'),
				import('@atlassian/conversation-assistant-ui-components/i18n/es'),
				import('@atlassian/conversation-assistant-chat-prompt-input/i18n/es'),
				import('@atlassian/home-recents/i18n/es'),
				import('@atlassian/home-assigned/i18n/es'),
				import('@atlassian/home-list/i18n/es'),
				import('@atlassian/home-list-ui/i18n/es'),
				import('@atlassian/goal-status/i18n/es'),
				import('@atlassian/project-status/i18n/es'),
			];
		case 'fi':
			return [
				import('@atlassian/afm-i18n-rovo-extension/i18n/fi'),
				import('@atlassian/conversation-assistant/i18n/fi'),
				import('@atlassian/conversation-assistant-agent/i18n/fi'),
				import('@atlassian/conversation-assistant-ui-components/i18n/fi'),
				import('@atlassian/conversation-assistant-chat-prompt-input/i18n/fi'),
				import('@atlassian/home-recents/i18n/fi'),
				import('@atlassian/home-assigned/i18n/fi'),
				import('@atlassian/home-list/i18n/fi'),
				import('@atlassian/home-list-ui/i18n/fi'),
				import('@atlassian/goal-status/i18n/fi'),
				import('@atlassian/project-status/i18n/fi'),
			];
		case 'fr':
			return [
				import('@atlassian/afm-i18n-rovo-extension/i18n/fr'),
				import('@atlassian/conversation-assistant/i18n/fr'),
				import('@atlassian/conversation-assistant-agent/i18n/fr'),
				import('@atlassian/conversation-assistant-ui-components/i18n/fr'),
				import('@atlassian/conversation-assistant-chat-prompt-input/i18n/fr'),
				import('@atlassian/home-recents/i18n/fr'),
				import('@atlassian/home-assigned/i18n/fr'),
				import('@atlassian/home-list/i18n/fr'),
				import('@atlassian/home-list-ui/i18n/fr'),
				import('@atlassian/goal-status/i18n/fr'),
				import('@atlassian/project-status/i18n/fr'),
			];
		case 'hr':
			// GCKO-2021: i18n missing for home-recents, home-assigned, home-list-ui, home-list, goal-status, project-status
			return [
				import('@atlassian/afm-i18n-rovo-extension/i18n/hr'),
				import('@atlassian/conversation-assistant/i18n/hr'),
				import('@atlassian/conversation-assistant-agent/i18n/hr'),
				import('@atlassian/conversation-assistant-ui-components/i18n/hr'),
				import('@atlassian/conversation-assistant-chat-prompt-input/i18n/hr'),
			];
		case 'hu':
			return [
				import('@atlassian/afm-i18n-rovo-extension/i18n/hu'),
				import('@atlassian/conversation-assistant/i18n/hu'),
				import('@atlassian/conversation-assistant-agent/i18n/hu'),
				import('@atlassian/conversation-assistant-ui-components/i18n/hu'),
				import('@atlassian/conversation-assistant-chat-prompt-input/i18n/hu'),
				import('@atlassian/home-recents/i18n/hu'),
				import('@atlassian/home-assigned/i18n/hu'),
				import('@atlassian/home-list/i18n/hu'),
				import('@atlassian/home-list-ui/i18n/hu'),
				import('@atlassian/goal-status/i18n/hu'),
				import('@atlassian/project-status/i18n/hu'),
			];
		case 'it':
			return [
				import('@atlassian/afm-i18n-rovo-extension/i18n/it'),
				import('@atlassian/conversation-assistant/i18n/it'),
				import('@atlassian/conversation-assistant-agent/i18n/it'),
				import('@atlassian/conversation-assistant-ui-components/i18n/it'),
				import('@atlassian/conversation-assistant-chat-prompt-input/i18n/it'),
				import('@atlassian/home-recents/i18n/it'),
				import('@atlassian/home-assigned/i18n/it'),
				import('@atlassian/home-list/i18n/it'),
				import('@atlassian/home-list-ui/i18n/it'),
				import('@atlassian/goal-status/i18n/it'),
				import('@atlassian/project-status/i18n/it'),
			];
		case 'ja':
			return [
				import('@atlassian/afm-i18n-rovo-extension/i18n/ja'),
				import('@atlassian/conversation-assistant/i18n/ja'),
				import('@atlassian/conversation-assistant-agent/i18n/ja'),
				import('@atlassian/conversation-assistant-ui-components/i18n/ja'),
				import('@atlassian/conversation-assistant-chat-prompt-input/i18n/ja'),
				import('@atlassian/home-recents/i18n/ja'),
				import('@atlassian/home-assigned/i18n/ja'),
				import('@atlassian/home-list/i18n/ja'),
				import('@atlassian/home-list-ui/i18n/ja'),
				import('@atlassian/goal-status/i18n/ja'),
				import('@atlassian/project-status/i18n/ja'),
			];
		case 'ko':
			return [
				import('@atlassian/afm-i18n-rovo-extension/i18n/ko'),
				import('@atlassian/conversation-assistant/i18n/ko'),
				import('@atlassian/conversation-assistant-agent/i18n/ko'),
				import('@atlassian/conversation-assistant-ui-components/i18n/ko'),
				import('@atlassian/conversation-assistant-chat-prompt-input/i18n/ko'),
				import('@atlassian/home-recents/i18n/ko'),
				import('@atlassian/home-assigned/i18n/ko'),
				import('@atlassian/home-list/i18n/ko'),
				import('@atlassian/home-list-ui/i18n/ko'),
				import('@atlassian/goal-status/i18n/ko'),
				import('@atlassian/project-status/i18n/ko'),
			];
		case 'nb':
			return [
				import('@atlassian/afm-i18n-rovo-extension/i18n/nb'),
				import('@atlassian/conversation-assistant/i18n/nb'),
				import('@atlassian/conversation-assistant-agent/i18n/nb'),
				import('@atlassian/conversation-assistant-ui-components/i18n/nb'),
				import('@atlassian/conversation-assistant-chat-prompt-input/i18n/nb'),
				import('@atlassian/home-recents/i18n/nb'),
				import('@atlassian/home-assigned/i18n/nb'),
				import('@atlassian/home-list/i18n/nb'),
				import('@atlassian/home-list-ui/i18n/nb'),
				import('@atlassian/goal-status/i18n/nb'),
				import('@atlassian/project-status/i18n/nb'),
			];
		case 'nl':
			return [
				import('@atlassian/afm-i18n-rovo-extension/i18n/nl'),
				import('@atlassian/conversation-assistant/i18n/nl'),
				import('@atlassian/conversation-assistant-agent/i18n/nl'),
				import('@atlassian/conversation-assistant-ui-components/i18n/nl'),
				import('@atlassian/conversation-assistant-chat-prompt-input/i18n/nl'),
				import('@atlassian/home-recents/i18n/nl'),
				import('@atlassian/home-assigned/i18n/nl'),
				import('@atlassian/home-list/i18n/nl'),
				import('@atlassian/home-list-ui/i18n/nl'),
				import('@atlassian/goal-status/i18n/nl'),
				import('@atlassian/project-status/i18n/nl'),
			];
		case 'pl':
			return [
				import('@atlassian/afm-i18n-rovo-extension/i18n/pl'),
				import('@atlassian/conversation-assistant/i18n/pl'),
				import('@atlassian/conversation-assistant-agent/i18n/pl'),
				import('@atlassian/conversation-assistant-ui-components/i18n/pl'),
				import('@atlassian/conversation-assistant-chat-prompt-input/i18n/pl'),
				import('@atlassian/home-recents/i18n/pl'),
				import('@atlassian/home-assigned/i18n/pl'),
				import('@atlassian/home-list/i18n/pl'),
				import('@atlassian/home-list-ui/i18n/pl'),
				import('@atlassian/goal-status/i18n/pl'),
				import('@atlassian/project-status/i18n/pl'),
			];
		case 'pt':
		case 'pt-BR':
			return [
				import('@atlassian/afm-i18n-rovo-extension/i18n/pt_BR'),
				import('@atlassian/conversation-assistant/i18n/pt_BR'),
				import('@atlassian/conversation-assistant-agent/i18n/pt_BR'),
				import('@atlassian/conversation-assistant-ui-components/i18n/pt_BR'),
				import('@atlassian/conversation-assistant-chat-prompt-input/i18n/pt_BR'),
				import('@atlassian/home-recents/i18n/pt_BR'),
				import('@atlassian/home-assigned/i18n/pt_BR'),
				import('@atlassian/home-list/i18n/pt_BR'),
				import('@atlassian/home-list-ui/i18n/pt_BR'),
				import('@atlassian/goal-status/i18n/pt_BR'),
				import('@atlassian/project-status/i18n/pt_BR'),
			];
		case 'ru':
			return [
				import('@atlassian/afm-i18n-rovo-extension/i18n/ru'),
				import('@atlassian/conversation-assistant/i18n/ru'),
				import('@atlassian/conversation-assistant-agent/i18n/ru'),
				import('@atlassian/conversation-assistant-ui-components/i18n/ru'),
				import('@atlassian/conversation-assistant-chat-prompt-input/i18n/ru'),
				import('@atlassian/home-recents/i18n/ru'),
				import('@atlassian/home-assigned/i18n/ru'),
				import('@atlassian/home-list/i18n/ru'),
				import('@atlassian/home-list-ui/i18n/ru'),
				import('@atlassian/goal-status/i18n/ru'),
				import('@atlassian/project-status/i18n/ru'),
			];
		case 'sl':
			// GCKO-2021: i18n missing for home-recents, home-assigned, home-list-ui, home-list, goal-status, project-status
			return [
				import('@atlassian/afm-i18n-rovo-extension/i18n/sl'),
				import('@atlassian/conversation-assistant/i18n/sl'),
				import('@atlassian/conversation-assistant-agent/i18n/sl'),
				import('@atlassian/conversation-assistant-ui-components/i18n/sl'),
				import('@atlassian/conversation-assistant-chat-prompt-input/i18n/sl'),
			];
		case 'sr':
			// GCKO-2021: i18n missing for home-recents, home-assigned, home-list-ui, home-list, goal-status, project-status
			return [
				import('@atlassian/afm-i18n-rovo-extension/i18n/sr'),
				import('@atlassian/conversation-assistant/i18n/sr_RS'),
				import('@atlassian/conversation-assistant-agent/i18n/sr_RS'),
				import('@atlassian/conversation-assistant-ui-components/i18n/sr_RS'),
				import('@atlassian/conversation-assistant-chat-prompt-input/i18n/sr_RS'),
			];
		case 'sv':
			return [
				import('@atlassian/afm-i18n-rovo-extension/i18n/sv'),
				import('@atlassian/conversation-assistant/i18n/sv'),
				import('@atlassian/conversation-assistant-agent/i18n/sv'),
				import('@atlassian/conversation-assistant-ui-components/i18n/sv'),
				import('@atlassian/conversation-assistant-chat-prompt-input/i18n/sv'),
				import('@atlassian/home-recents/i18n/sv'),
				import('@atlassian/home-assigned/i18n/sv'),
				import('@atlassian/home-list/i18n/sv'),
				import('@atlassian/home-list-ui/i18n/sv'),
				import('@atlassian/goal-status/i18n/sv'),
				import('@atlassian/project-status/i18n/sv'),
			];
		case 'th':
			return [
				import('@atlassian/afm-i18n-rovo-extension/i18n/th'),
				import('@atlassian/conversation-assistant/i18n/th'),
				import('@atlassian/conversation-assistant-agent/i18n/th'),
				import('@atlassian/conversation-assistant-ui-components/i18n/th'),
				import('@atlassian/conversation-assistant-chat-prompt-input/i18n/th'),
				import('@atlassian/home-recents/i18n/th'),
				import('@atlassian/home-assigned/i18n/th'),
				import('@atlassian/home-list/i18n/th'),
				import('@atlassian/home-list-ui/i18n/th'),
				import('@atlassian/goal-status/i18n/th'),
				import('@atlassian/project-status/i18n/th'),
			];
		case 'tr':
			return [
				import('@atlassian/afm-i18n-rovo-extension/i18n/tr'),
				import('@atlassian/conversation-assistant/i18n/tr'),
				import('@atlassian/conversation-assistant-agent/i18n/tr'),
				import('@atlassian/conversation-assistant-ui-components/i18n/tr'),
				import('@atlassian/conversation-assistant-chat-prompt-input/i18n/tr'),
				import('@atlassian/home-recents/i18n/tr'),
				import('@atlassian/home-assigned/i18n/tr'),
				import('@atlassian/home-list/i18n/tr'),
				import('@atlassian/home-list-ui/i18n/tr'),
				import('@atlassian/goal-status/i18n/tr'),
				import('@atlassian/project-status/i18n/tr'),
			];
		case 'uk':
			return [
				import('@atlassian/afm-i18n-rovo-extension/i18n/uk'),
				import('@atlassian/conversation-assistant/i18n/uk'),
				import('@atlassian/conversation-assistant-agent/i18n/uk'),
				import('@atlassian/conversation-assistant-ui-components/i18n/uk'),
				import('@atlassian/conversation-assistant-chat-prompt-input/i18n/uk'),
				import('@atlassian/home-recents/i18n/uk'),
				import('@atlassian/home-assigned/i18n/uk'),
				import('@atlassian/home-list/i18n/uk'),
				import('@atlassian/home-list-ui/i18n/uk'),
				import('@atlassian/goal-status/i18n/uk'),
				import('@atlassian/project-status/i18n/uk'),
			];
		case 'vi':
			return [
				import('@atlassian/afm-i18n-rovo-extension/i18n/vi'),
				import('@atlassian/conversation-assistant/i18n/vi'),
				import('@atlassian/conversation-assistant-agent/i18n/vi'),
				import('@atlassian/conversation-assistant-ui-components/i18n/vi'),
				import('@atlassian/conversation-assistant-chat-prompt-input/i18n/vi'),
				import('@atlassian/home-recents/i18n/vi'),
				import('@atlassian/home-assigned/i18n/vi'),
				import('@atlassian/home-list/i18n/vi'),
				import('@atlassian/home-list-ui/i18n/vi'),
				import('@atlassian/goal-status/i18n/vi'),
				import('@atlassian/project-status/i18n/vi'),
			];
		case 'zh':
			return [
				import('@atlassian/afm-i18n-rovo-extension/i18n/zh'),
				import('@atlassian/conversation-assistant/i18n/zh'),
				import('@atlassian/conversation-assistant-agent/i18n/zh'),
				import('@atlassian/conversation-assistant-ui-components/i18n/zh'),
				import('@atlassian/conversation-assistant-chat-prompt-input/i18n/zh'),
				import('@atlassian/home-recents/i18n/zh'),
				import('@atlassian/home-assigned/i18n/zh'),
				import('@atlassian/home-list/i18n/zh'),
				import('@atlassian/home-list-ui/i18n/zh'),
				import('@atlassian/goal-status/i18n/zh'),
				import('@atlassian/project-status/i18n/zh'),
			];
		case 'zh-TW':
			return [
				import('@atlassian/afm-i18n-rovo-extension/i18n/zh_TW'),
				import('@atlassian/conversation-assistant/i18n/zh_TW'),
				import('@atlassian/conversation-assistant-agent/i18n/zh_TW'),
				import('@atlassian/conversation-assistant-ui-components/i18n/zh_TW'),
				import('@atlassian/conversation-assistant-chat-prompt-input/i18n/zh_TW'),
				import('@atlassian/home-recents/i18n/zh_TW'),
				import('@atlassian/home-assigned/i18n/zh_TW'),
				import('@atlassian/home-list/i18n/zh_TW'),
				import('@atlassian/home-list-ui/i18n/zh_TW'),
				import('@atlassian/goal-status/i18n/zh_TW'),
				import('@atlassian/project-status/i18n/zh_TW'),
			];
		case 'en':
		case 'en-US':
		default:
			return [import('@atlassian/afm-i18n-rovo-extension/i18n/en')];
	}
}

export async function loadLocaleData(locale: string): Promise<any> {
	const localeToLoad = getSupportedLocale(locale);

	try {
		const results = await Promise.all(getMessageLoaders(localeToLoad));
		return Object.assign({}, ...results.map(resolveMessages));
	} catch (error) {
		logError({
			error,
			impact: 'Failed to import locale strings, attempting to load English fallback',
		});
		return resolveMessages(await import('@atlassian/afm-i18n-rovo-extension/i18n/en'));
	}
}
